import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  ClipboardList, ChevronDown, ChevronUp, Trash2, Printer, 
  School, Calendar, User, BarChart3, X
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { LOGO_BASE64 } from '../assets/logoBase64';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Labels per le risposte
const LABELS = {
  q1: {
    reti_plastica: 'Reti di plastica',
    bottiglie_tappi: 'Bottiglie/Tappi',
    microplastiche: 'Microplastiche'
  },
  q3: {
    si_sapevo: 'Sì, lo sapevo',
    no_scoperto: 'No, l\'ho scoperto con il laboratorio'
  },
  q4: {
    si_fatto_caso: 'Sì, ci ho fatto caso',
    non_ancora: 'Non ancora, ma inizierò oggi'
  },
  q5: {
    meno_bottiglie: 'Ho usato meno bottiglie di plastica',
    spiegato_genitori: 'Ho spiegato ai genitori cos\'è una rete fantasma',
    raccolto_rifiuto: 'Ho raccolto un rifiuto di plastica',
    riutilizzare: 'Ho pensato a riutilizzare un oggetto'
  },
  q6: {
    si_so_cercare: 'Sì, ora so cosa cercare',
    non_sicuro: 'Non sono sicuro'
  }
};

const QuestionnaireList = () => {
  const { token, isAdmin } = useAuth();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filterSchool, setFilterSchool] = useState('');
  const [showStats, setShowStats] = useState(false);

  const fetchQuestionnaires = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/questionnaires`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestionnaires(response.data.questionnaires || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin]);

  useEffect(() => {
    fetchQuestionnaires();
  }, [fetchQuestionnaires]);

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo questionario?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/questionnaires/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Questionario eliminato');
      fetchQuestionnaires();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Genera PDF singolo questionario
  const generatePDF = (q) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = margin;

    // Header
    if (LOGO_BASE64) {
      try {
        doc.addImage(LOGO_BASE64, 'JPEG', margin, yPos, 25, 25);
      } catch (e) {}
    }

    doc.setFontSize(18);
    doc.setTextColor(0, 128, 128);
    doc.text('QUESTIONARIO MAREDU', pageWidth / 2, yPos + 12, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Respiro Blu - Educazione Ambientale', pageWidth / 2, yPos + 20, { align: 'center' });
    
    yPos += 35;
    
    // Linea
    doc.setDrawColor(0, 128, 128);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;

    // Info studente/scuola
    doc.setFillColor(240, 248, 255);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 25, 3, 3, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Scuola:', margin + 5, yPos + 8);
    doc.setFont('helvetica', 'normal');
    doc.text(q.school_name || '-', margin + 25, yPos + 8);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Classe:', margin + 80, yPos + 8);
    doc.setFont('helvetica', 'normal');
    doc.text(q.class_name || '-', margin + 100, yPos + 8);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Data Laboratorio:', margin + 5, yPos + 16);
    doc.setFont('helvetica', 'normal');
    doc.text(q.lab_date ? new Date(q.lab_date).toLocaleDateString('it-IT') : '-', margin + 45, yPos + 16);
    
    if (q.student_name) {
      doc.setFont('helvetica', 'bold');
      doc.text('Studente:', margin + 80, yPos + 16);
      doc.setFont('helvetica', 'normal');
      doc.text(q.student_name, margin + 105, yPos + 16);
    }
    
    yPos += 35;

    // Domande e risposte
    const questions = [
      { num: 1, q: 'Quale materiale ti è sembrato più pericoloso per i pesci?', a: LABELS.q1[q.q1_materiale_pericoloso] || q.q1_materiale_pericoloso },
      { num: 2, q: 'Cosa ti ha colpito di più della storia di Rafa?', a: q.q2_storia_rafa || '-' },
      { num: 3, q: 'Sapevi delle "reti fantasma"?', a: LABELS.q3[q.q3_reti_fantasma] || q.q3_reti_fantasma },
      { num: 4, q: 'Hai fatto attenzione alla plastica usata in famiglia?', a: LABELS.q4[q.q4_plastica_famiglia] || q.q4_plastica_famiglia },
      { num: 5, q: 'Azioni fatte dopo il laboratorio:', a: (q.q5_azioni || []).map(a => LABELS.q5[a] || a).join(', ') || 'Nessuna' },
      { num: 6, q: 'Sapresti riconoscere un rifiuto dal mare?', a: LABELS.q6[q.q6_riconoscere_rifiuto] || q.q6_riconoscere_rifiuto },
      { num: 7, q: 'Cos\'è l\'inquinamento secondo te?', a: q.q7_inquinamento || '-' },
      { num: 8, q: 'Cosa ti è piaciuto di più dell\'incontro?', a: q.q8_piaciuto_piu || '-' }
    ];

    questions.forEach((item) => {
      if (yPos > 260) {
        doc.addPage();
        yPos = margin;
      }
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 100, 100);
      doc.text(`${item.num}. ${item.q}`, margin, yPos);
      yPos += 6;
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      
      // Gestisci testo lungo
      const lines = doc.splitTextToSize(item.a, pageWidth - 2 * margin - 5);
      lines.forEach(line => {
        if (yPos > 270) {
          doc.addPage();
          yPos = margin;
        }
        doc.text(line, margin + 5, yPos);
        yPos += 5;
      });
      
      yPos += 5;
    });

    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 10;
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')} - Respiro Blu`, pageWidth / 2, footerY, { align: 'center' });

    // Salva
    const studentSuffix = q.student_name ? `_${q.student_name.replace(/\s+/g, '_')}` : '';
    doc.save(`Questionario_${q.school_name.replace(/\s+/g, '_')}_${q.class_name}${studentSuffix}.pdf`);
    toast.success('PDF generato!');
  };

  const filteredQuestionnaires = filterSchool 
    ? questionnaires.filter(q => q.school_name === filterSchool)
    : questionnaires;

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header con stats */}
      <div className="flex items-center justify-between">
        <h4 className="text-white font-semibold flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-emerald-400" />
          Questionari Ricevuti ({questionnaires.length})
        </h4>
        <button
          onClick={() => setShowStats(!showStats)}
          className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-sm rounded-lg flex items-center gap-1"
        >
          <BarChart3 className="w-4 h-4" />
          Statistiche
        </button>
      </div>

      {/* Statistiche aggregate */}
      {showStats && stats.total > 0 && (
        <div className="bg-slate-800/50 border border-emerald-500/30 rounded-xl p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-emerald-400 font-medium">Statistiche Aggregate</h5>
            <button onClick={() => setShowStats(false)} className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-slate-900/50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-xs text-slate-400">Questionari</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-white">{stats.schools_count}</p>
              <p className="text-xs text-slate-400">Scuole</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-cyan-400">
                {stats.q3_stats?.no_scoperto || 0}
              </p>
              <p className="text-xs text-slate-400">Hanno imparato sulle reti</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-emerald-400">
                {stats.q4_stats?.si_fatto_caso || 0}
              </p>
              <p className="text-xs text-slate-400">Attenti alla plastica</p>
            </div>
          </div>

          {/* Azioni più comuni (Q5) */}
          {stats.q5_stats && Object.keys(stats.q5_stats).length > 0 && (
            <div>
              <p className="text-sm text-slate-400 mb-2">Azioni più comuni dopo il laboratorio:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.q5_stats).sort((a, b) => b[1] - a[1]).map(([key, count]) => (
                  <span key={key} className="px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded-full">
                    {LABELS.q5[key] || key}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filtro per scuola */}
      {stats.schools && stats.schools.length > 1 && (
        <div className="flex items-center gap-2">
          <School className="w-4 h-4 text-slate-400" />
          <select
            value={filterSchool}
            onChange={(e) => setFilterSchool(e.target.value)}
            className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
          >
            <option value="">Tutte le scuole</option>
            {stats.schools.map(school => (
              <option key={school} value={school}>{school}</option>
            ))}
          </select>
        </div>
      )}

      {/* Lista questionari */}
      {filteredQuestionnaires.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>Nessun questionario ricevuto</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredQuestionnaires.map((q) => (
            <div key={q.id} className="bg-slate-900/60 border border-slate-700/50 rounded-xl overflow-hidden">
              <div 
                className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-800/50"
                onClick={() => toggleExpand(q.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <ClipboardList className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">
                      {q.school_name} - {q.class_name}
                      {q.student_name && <span className="text-slate-400"> ({q.student_name})</span>}
                    </p>
                    <p className="text-xs text-slate-500">
                      {q.lab_date ? new Date(q.lab_date).toLocaleDateString('it-IT') : '-'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); generatePDF(q); }}
                    className="p-1.5 text-cyan-400 hover:bg-cyan-500/20 rounded"
                    title="Stampa PDF"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(q.id); }}
                    className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                    title="Elimina"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {expanded[q.id] ? (
                    <ChevronUp className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  )}
                </div>
              </div>
              
              {expanded[q.id] && (
                <div className="border-t border-slate-700/50 p-4 bg-slate-800/30 space-y-3 text-sm">
                  <div>
                    <p className="text-slate-500 text-xs">1. Materiale più pericoloso:</p>
                    <p className="text-slate-300">{LABELS.q1[q.q1_materiale_pericoloso] || q.q1_materiale_pericoloso}</p>
                  </div>
                  {q.q2_storia_rafa && (
                    <div>
                      <p className="text-slate-500 text-xs">2. Cosa ti ha colpito di Rafa:</p>
                      <p className="text-slate-300">{q.q2_storia_rafa}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-slate-500 text-xs">3. Sapevi delle reti fantasma?</p>
                    <p className="text-slate-300">{LABELS.q3[q.q3_reti_fantasma] || q.q3_reti_fantasma}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">4. Attenzione alla plastica:</p>
                    <p className="text-slate-300">{LABELS.q4[q.q4_plastica_famiglia] || q.q4_plastica_famiglia}</p>
                  </div>
                  {q.q5_azioni && q.q5_azioni.length > 0 && (
                    <div>
                      <p className="text-slate-500 text-xs">5. Azioni dopo il laboratorio:</p>
                      <ul className="text-slate-300 list-disc list-inside">
                        {q.q5_azioni.map((a, i) => (
                          <li key={i}>{LABELS.q5[a] || a}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <p className="text-slate-500 text-xs">6. Riconoscere rifiuti dal mare:</p>
                    <p className="text-slate-300">{LABELS.q6[q.q6_riconoscere_rifiuto] || q.q6_riconoscere_rifiuto}</p>
                  </div>
                  {q.q7_inquinamento && (
                    <div>
                      <p className="text-slate-500 text-xs">7. Cos'è l'inquinamento:</p>
                      <p className="text-slate-300">{q.q7_inquinamento}</p>
                    </div>
                  )}
                  {q.q8_piaciuto_piu && (
                    <div>
                      <p className="text-slate-500 text-xs">8. Cosa ti è piaciuto di più:</p>
                      <p className="text-slate-300">{q.q8_piaciuto_piu}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuestionnaireList;
